import { IndexesFormType } from 'components/indexes-form-inputs/types'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import { CheckHasMeterAndReadingInputs, ContractDataWithIndexes, FormatContractStatus, MeterIndexType } from 'types/contracts'
import { SimplifiedContractStatus } from 'types/types'
import { NormalizedEntities } from 'store/contact/types.ts'
import { getPersistedItem, setPersistedItem } from 'utils/persistedStorage.ts'
import { LAST_OPENED_CONTRACT_STORAGE_KEY_PREFIX } from 'constants/constants.ts'
import {
  BillingContractWithServiceContracts,
  CONTRACT_STATUSES,
  ContractStatus,
  COUNTER_TYPES,
  Direction,
  DIRECTIONS,
  PHYSICAL_METER_TYPES,
  PhysicalMeterType,
  ServiceContract,
  ServiceContractElectricity,
  ServiceContractGas,
  SITUATIONS,
  TIMEFRAME_CODES
} from '@boltenergy-be/shared'

dayjs.extend(isBetween)

/**
 * Checks if the given contract is at least a month old (both start & subscription date)
 *
 * @param {ServiceContract} contract
 * @param {number} ageInMonths
 * @returns {boolean}
 */
export const contractIsOlderThanGivenMonths = (contract: ServiceContract, ageInMonths: number): boolean => {
  if (!contract) {
    return false
  }

  return (
    dayjs().diff(dayjs(contract.detail.contractualStartDate), 'month') >= ageInMonths &&
    dayjs().diff(dayjs(contract.detail.subscriptionDate), 'month') >= ageInMonths
  )
}

/**
 * Checks if the given contract has a subscriptionDate older than 1 day (both start & subscription date)
 *
 * @param {ServiceContract} contract
 * @returns {boolean}
 */
export const contractSubscriptionDateOlderThanOneDay = (contract: ServiceContract): boolean => {
  if (!contract) {
    return false
  }

  return dayjs().diff(dayjs(contract.detail.subscriptionDate), 'day') >= 1
}

/**
 * Checks if the meter and number inputs are visible
 * @param {CheckHasMeterAndReadingInputs} params
 * @returns {boolean}
 */
export const checkHasMeterAndNumberInputs = ({ situation, counterType, contractStartDate }: CheckHasMeterAndReadingInputs): boolean => {
  return situation === SITUATIONS.move && counterType === COUNTER_TYPES.analog && !dayjs(contractStartDate).isAfter(dayjs())
}

/**
 * Helper function that checks if the contract has a digital meter or not
 * @param {ServiceContract} contract
 * @returns {boolean}
 */
export const hasDigitalMeter = (contract: ServiceContract): boolean => {
  return (
    !!contract.deliveryPoint.meterType &&
    ([PHYSICAL_METER_TYPES.smartMeter, PHYSICAL_METER_TYPES.electronicMeter] as PhysicalMeterType[]).includes(
      contract.deliveryPoint.meterType
    )
  )
}

/**
 * Helper function that checks if the contract has a digital meter or not
 * @param {ServiceContract} contract
 * @returns {boolean}
 */
export const hasSmartMeter = (contract: ServiceContract): boolean => {
  return contract.deliveryPoint.meterType === PHYSICAL_METER_TYPES.smartMeter
}

/**
 * Generates the contracts data with indexes based on the given parameters
 *
 * @param {IndexesFormType} indexes
 * @param {ServiceContractElectricity} electricityContract
 * @param {ServiceContractGas=} gasContract
 * @param {boolean=} isInFuture
 * @returns {ContractDataWithIndexes[]}
 */
export const generateContractsDataWithIndexes = (
  indexes: IndexesFormType,
  electricityContract: ServiceContractElectricity,
  gasContract?: ServiceContractGas,
  isInFuture?: boolean
): ContractDataWithIndexes[] => {
  const contractsDataWithIndexes: ContractDataWithIndexes[] = []

  if (!hasDigitalMeter(electricityContract) && electricityContract?.deliveryPoint?.previousIndex) {
    // Generate electricity indexes
    if (indexes.electricity) {
      contractsDataWithIndexes.push({
        contractNumber: electricityContract.contractNumber,
        ean: electricityContract.deliveryPoint.ean,
        indexes: isInFuture
          ? undefined
          : electricityContract.deliveryPoint.previousIndex?.registers
              .filter((register) => register.timeframeCode !== TIMEFRAME_CODES.notUsed)
              .map((register, i) => ({
                meterNumber: electricityContract.deliveryPoint.previousIndex!.meterNumber,
                registerName: register.registerName,
                timeframeCode: register.timeframeCode,
                value: indexes.electricity![i],
                direction: register.direction
              }))
      })
    }

    // Generate gas indexes if necessary
    if (gasContract && indexes.gas) {
      contractsDataWithIndexes.push({
        contractNumber: gasContract.contractNumber,
        ean: gasContract.deliveryPoint.ean,
        indexes: isInFuture
          ? undefined
          : gasContract?.deliveryPoint?.previousIndex?.registers
              ?.filter((register) => register.timeframeCode !== TIMEFRAME_CODES.notUsed)
              ?.map((register, i) => ({
                meterNumber: gasContract?.deliveryPoint?.previousIndex!.meterNumber,
                registerName: register.registerName,
                timeframeCode: register.timeframeCode,
                value: indexes.gas![i],
                direction: register.direction
              }))
      })
    }
  } else {
    // Generate electricity indexes (always present)
    contractsDataWithIndexes.push({
      contractNumber: electricityContract.contractNumber,
      ean: electricityContract.deliveryPoint.ean
    })

    // Generate gas indexes if necessary
    if (gasContract) {
      contractsDataWithIndexes.push({
        contractNumber: gasContract.contractNumber,
        ean: gasContract.deliveryPoint.ean
      })
    }
  }

  return contractsDataWithIndexes
}

/**
 * Returns the digital meter register name based on the given direction & meterIndexType
 *
 * @param {Direction} direction
 * @param {MeterIndexType} meterIndexType
 * @returns {string}
 */
export const getDigitalMeterRegisterName = (direction: Direction, meterIndexType: MeterIndexType): string => {
  if ([MeterIndexType.DAY, MeterIndexType.NIGHT].includes(meterIndexType)) {
    return ` (${direction === DIRECTIONS.consumption ? '1' : '2'}.8.${meterIndexType === MeterIndexType.DAY ? '1' : '2'})`
  }

  return ''
}

/**
 * Checks if the given billing contract is active (both electricity & gas if available)
 *
 * @param {BillingContractWithServiceContracts} billingContract
 * @returns {boolean}
 */
export const isInActiveAddress = (billingContract: BillingContractWithServiceContracts): boolean => {
  return (
    !isActiveContract(billingContract.serviceContracts.electricity) &&
    (billingContract.serviceContracts.gas ? !isActiveContract(billingContract.serviceContracts.gas) : true)
  )
}

/**
 * Checks if the given contract is active
 *
 * @param {ServiceContract} contract
 * @param {readonly (typeof CONTRACT_STATUSES.active | typeof CONTRACT_STATUSES.effective)[]} activeStatuses
 * @returns {boolean}
 */
export const isActiveContract = (
  contract?: ServiceContract,
  activeStatuses: readonly (typeof CONTRACT_STATUSES.active | typeof CONTRACT_STATUSES.effective)[] = [
    CONTRACT_STATUSES.active,
    CONTRACT_STATUSES.effective
  ]
): boolean => {
  // Guard clause for undefined contract
  if (!contract) {
    return false
  }

  // Guard clause for empty active statuses
  if (!activeStatuses.length) {
    return false
  }

  return (
    // Contract start date has passed AND
    dayjs().isSameOrAfter(contract.detail.contractualStartDate) &&
    // Contract has 'Active' or 'Effective' status OR Contract has 'Terminated' status but end date has not passed yet
    (checkContractStatus(contract, activeStatuses as ContractStatus[]) ||
      (checkContractStatus(contract, CONTRACT_STATUSES.terminated) && dayjs().isSameOrBefore(contract.detail.effectiveEndDate)))
  )
}

/**
 * Checks if the given contract has status "TERMINATED" and is past the effectiveEndDate
 * @param {ServiceContractElectricity} contract
 * @returns {boolean}
 */
export const isTerminatedContractPastEndDate = (contract?: ServiceContractElectricity): boolean => {
  if (!contract) return true

  return checkContractStatus(contract, CONTRACT_STATUSES.terminated) && dayjs().isAfter(contract.detail.effectiveEndDate)
}

/**
 * Checks if the given contract is set to be terminated within a month
 *
 * @param {ServiceContract} contract
 * @returns {boolean}
 */
export const isCloseToTermination = (contract: ServiceContract): boolean => {
  return (
    checkContractStatus(contract, CONTRACT_STATUSES.terminated) &&
    dayjs(contract.detail.effectiveEndDate).isBetween(dayjs(), dayjs().add(1, 'month'), 'day', '[]')
  )
}

/**
 * Checks if the given contract is terminated or canceled
 * @param {ServiceContract} contract
 * @returns {boolean}
 */
export const isInactiveContract = (contract: ServiceContract): boolean => {
  return checkContractStatus(contract, [CONTRACT_STATUSES.terminated, CONTRACT_STATUSES.cancelled])
}

/**
 * Check if the given contract matches the given status(ses)
 * @param {ServiceContract} contract
 * @param {ContractStatus | Array<ContractStatus>} status
 * @returns {boolean}
 */
export const checkContractStatus = (contract: ServiceContract, status: ContractStatus | Array<ContractStatus>): boolean => {
  if (Array.isArray(status)) {
    return status.some((s) => contract.detail.status === s)
  }

  return contract.detail.status === status
}

/**
 * Returns the first active electricity contract
 *
 * @param {NormalizedEntities['billingContracts']} billingContracts
 * @returns {BillingContract | undefined}
 */
export const getAccountsWithActiveElectricityContract = (
  billingContracts: NormalizedEntities['billingContracts']
): Map<string, BillingContractWithServiceContracts[]> => {
  const accountsWithActiveElectricityContract = new Map<string, BillingContractWithServiceContracts[]>()

  for (const billingContractKey in billingContracts) {
    const billingContract = billingContracts[billingContractKey]
    const { customerNumber } = billingContract

    if (isActiveContract(billingContract.serviceContracts.electricity)) {
      if (!accountsWithActiveElectricityContract.has(customerNumber)) {
        accountsWithActiveElectricityContract.set(customerNumber, [])
      }

      accountsWithActiveElectricityContract.get(customerNumber)?.push(billingContract)
    }
  }

  return accountsWithActiveElectricityContract
}

/**
 * Group billing contract id's by active addresses
 * @param {string[]} billingContractNumbers
 * @param {NormalizedEntities['billingContracts']} normalizedContracts
 * @returns {{active: string[], inactive: string[]}}
 */
export const groupBillingContractsByActiveAddresses = (
  billingContractNumbers: string[],
  normalizedContracts: NormalizedEntities['billingContracts']
): { active: string[]; inactive: string[] } => {
  return billingContractNumbers.reduce(
    (filteredContracts: { active: string[]; inactive: string[] }, contractNumber) => {
      const billingContract = normalizedContracts[contractNumber]

      // An account can reference a contract that is missing from the normalized store, e.g. after it was ignored or removed
      if (!billingContract) return filteredContracts

      const isActiveAddress = !isInActiveAddress(billingContract)
      if (isActiveAddress) {
        filteredContracts.active.push(contractNumber)
      } else {
        filteredContracts.inactive.push(contractNumber)
      }

      return filteredContracts
    },
    {
      active: [],
      inactive: []
    }
  )
}

/**
 * Picks the billing contract to select for an account: the first one with an active electricity contract, falling back
 * to the first contract that is actually present in the store. Returns undefined when the account has none to select,
 * so callers never dispatch a contract number the store cannot resolve.
 *
 * @param {string[]} billingContractNumbers
 * @param {NormalizedEntities['billingContracts']} normalizedContracts
 * @returns {string | undefined}
 */
export const getBillingContractToSelect = (
  billingContractNumbers: string[],
  normalizedContracts: NormalizedEntities['billingContracts']
): string | undefined => {
  const known = billingContractNumbers.filter((contractNumber) => !!normalizedContracts[contractNumber])

  return known.find((contractNumber) => isActiveContract(normalizedContracts[contractNumber].serviceContracts.electricity)) ?? known[0]
}

/**
 * Determines the contracts simplified status
 * @param {ServiceContract} contract
 * @returns {SimplifiedContractStatus}
 */
const determineContractStatusType = (contract: ServiceContract): SimplifiedContractStatus => {
  switch (contract.detail.status) {
    case CONTRACT_STATUSES.effective:
      return SimplifiedContractStatus.ACTIVE
    case CONTRACT_STATUSES.terminated:
      return SimplifiedContractStatus.TERMINATED
    case CONTRACT_STATUSES.cancelled:
      return SimplifiedContractStatus.CANCELLED
    case CONTRACT_STATUSES.active:
    case CONTRACT_STATUSES.ilcOngoing:
    case CONTRACT_STATUSES.inactive:
      return SimplifiedContractStatus.IN_PROGRESS
    default:
      return SimplifiedContractStatus.INACTIVE
  }
}

/**
 * Calculates the contract status based on the given contract
 * @param {ServiceContract} contract
 * @returns {FormatContractStatus}
 */
export const formatContractStatus = (contract: ServiceContract): FormatContractStatus => {
  const simplifiedStatus = determineContractStatusType(contract)
  const {
    detail: { effectiveEndDate, contractualStartDate }
  } = contract

  switch (simplifiedStatus) {
    case SimplifiedContractStatus.ACTIVE: {
      return {
        key: dayjs(contractualStartDate).isAfter(dayjs()) ? 'startingOn' : 'activeSince',
        type: 'positive',
        date: dayjs(contractualStartDate).toISOString()
      }
    }
    case SimplifiedContractStatus.TERMINATED: {
      const date = dayjs(effectiveEndDate).toISOString()
      return dayjs(effectiveEndDate).isAfter(dayjs())
        ? { date, key: 'terminatingOn', type: 'warning' }
        : { date, key: 'terminatedSince', type: 'neutral' }
    }
    default:
      return { key: simplifiedStatus, type: 'neutral' }
  }
}

/**
 * Check if the given contract has a budget meter
 * @param {ServiceContract} [contract]
 * @returns {boolean}
 */
export const checkHasBudgetMeter = (contract?: ServiceContract): boolean => {
  return contract ? contract.deliveryPoint.meterType === PHYSICAL_METER_TYPES.budgetMeter : false
}

/**
 * Gets the last opened billing contract number of an account
 *
 * @param {string} customerNumber
 * @returns {string | undefined}
 */
export const getLastOpenedContract = (customerNumber: string): string | undefined => {
  return getPersistedItem(`${LAST_OPENED_CONTRACT_STORAGE_KEY_PREFIX}${customerNumber}`, customerNumber)
}

/**
 * Persists the last opened billing contract number of an account
 *
 * @param {string} customerNumber
 * @param {string} contractNumber
 */
export const setLastOpenedContract = (customerNumber: string, contractNumber: string): void => {
  setPersistedItem(`${LAST_OPENED_CONTRACT_STORAGE_KEY_PREFIX}${customerNumber}`, contractNumber)
}
